import { memo, useRef } from "react";
import {
  Hash,
  PencilSimple,
  Trash,
  DotsThree,
  Archive,
  UserPlus,
  Handshake,
  Headphones,
  Bell,
  BellSlash,
  SignOut,
} from "@/components/ui/icons";
import { cn } from "@/lib/utils";
import { NavRow } from "@/components/ui/nav-row";

// Calls are gated by an env var (VITE_FEATURE_CALLS). When "false"
// (e.g. local dev — see scripts/dev-local.mjs), the huddle action
// silently no-ops, so we suppress the hover icon entirely. Read the
// flag directly here rather than via `useCall().callsEnabled` so
// ChannelItem stays renderable outside a `<CallProvider>` (the unit
// test in ChannelItem.test.tsx mounts the row without one).
const CALLS_ENABLED = import.meta.env.VITE_FEATURE_CALLS !== "false";
import { RENDER_DEBUG } from "@/lib/debug-flags";
import { useRenderDiff } from "@/lib/perf/use-render-diff";
import { csCountRender } from "@/lib/channel-switch-profile";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { toast } from "sonner";
import { ToolbarIconButton } from "@/components/ui/toolbar-icon-button";

export type ChannelLike = {
  id: string;
  name: string;
  is_archived?: boolean | null;
};

/**
 * Stable action callbacks for a ChannelItem. Passed as a single object so it
 * stays referentially stable across Sidebar re-renders — prevents the 9-callback
 * cascade that was forcing every ChannelItem to re-render on every selector
 * flip in Sidebar.
 */
export interface ChannelActions {
  select: (channel: ChannelLike) => void;
  edit: (channel: ChannelLike) => void;
  invite: (channel: ChannelLike) => void;
  toggleMute: (channel: ChannelLike) => void;
  toggleArchive: (channel: ChannelLike) => void;
  leave: (channel: ChannelLike) => void;
  remove: (channel: ChannelLike) => void;
  notifyChange: (channel: ChannelLike, notify: string) => void;
  /** Slack-Huddles-style one-click join — fires a call into this
   *  channel without requiring the user to first navigate into it.
   *  Implementation pipes through `useCall().joinCall()` higher up. */
  huddle: (channel: ChannelLike) => void;
}

interface ChannelItemProps {
  channel: ChannelLike;
  isSelected: boolean;
  isUnread: boolean;
  isMuted: boolean;
  mentionCount: number;
  membership: { notify?: string } | null | undefined;
  canManage: boolean;
  canDelete: boolean;
  hasCollaborators?: boolean;
  actions: ChannelActions;
}

export const ChannelItem = memo(function ChannelItem({
  channel,
  isSelected,
  isUnread,
  isMuted,
  mentionCount,
  membership,
  canManage,
  canDelete,
  hasCollaborators,
  actions,
}: ChannelItemProps) {
  csCountRender("ChannelItem");

  // ─── Render diagnostics ─────────────────────────────────────────
  const renderCountRef = useRef(0);
  // eslint-disable-next-line react-hooks/refs
  renderCountRef.current += 1;
  if (RENDER_DEBUG)
    console.debug(
      // eslint-disable-next-line react-hooks/refs
      `[ChannelItem:${channel.name}] render #${renderCountRef.current}`,
      `sel=${isSelected} unread=${isUnread} mention=${mentionCount}`,
    );
  useRenderDiff(`ChannelItem:${channel.name}`, {
    channel,
    isSelected,
    isUnread,
    isMuted,
    mentionCount,
    membership,
    canManage,
    canDelete,
    hasCollaborators,
    actions,
  });

  const handleCopyLink = () => {
    const link = `${window.location.origin}/?channel=${channel.id}`;
    navigator.clipboard
      .writeText(link)
      .then(() => toast.success("Link copied"))
      .catch(() => toast.error("Couldn't copy link"));
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div className="group/ch relative">
          <NavRow
            data-sidebar-channel-id={channel.id}
            isSelected={isSelected}
            onClick={() => actions.select(channel)}
            className="pr-[4.25rem]"
          >
            <Hash
              size={14}
              className={cn("text-muted-foreground", isMuted && "opacity-40")}
            />
            <span
              className={cn(
                "min-w-0 flex-1 truncate",
                isUnread
                  ? "font-bold text-sidebar-unread-foreground"
                  : "text-muted-foreground",
                isMuted && !isUnread && "text-muted-foreground",
              )}
            >
              {channel.name}
            </span>
            {hasCollaborators && <Handshake size={12} className="shrink-0" />}
            {mentionCount > 0 && (
              <span className="relative z-20 ml-auto bg-destructive text-primary-foreground text-[10px] px-1.5 py-0.5 rounded-full font-semibold shrink-0 min-w-[18px] text-center">
                @{mentionCount}
              </span>
            )}
          </NavRow>
          <div className="absolute right-1 top-1/2 z-10 hidden -translate-y-1/2 items-center gap-1 group-hover/ch:flex has-[[data-state=open]]:flex">
            {CALLS_ENABLED && (
              <ToolbarIconButton
                onClick={(e) => {
                  e.stopPropagation();
                  actions.huddle(channel);
                }}
                className="h-7 w-7"
                title="Start a huddle"
                aria-label={`Start a huddle in #${channel.name}`}
              >
                <Headphones size={14} className="text-muted-foreground" />
              </ToolbarIconButton>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <ToolbarIconButton
                  className="h-7 w-7 data-[state=open]:bg-foreground/10"
                  aria-label={`Actions for #${channel.name}`}
                >
                  <DotsThree size={14} className="text-muted-foreground" />
                </ToolbarIconButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canManage && (
                  <>
                    <DropdownMenuItem
                      onClick={() => actions.edit(channel)}
                      className="cursor-pointer"
                    >
                      <PencilSimple size={16} className="mr-2" />
                      Edit channel
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => actions.invite(channel)}
                      className="cursor-pointer"
                    >
                      <UserPlus size={16} className="mr-2" />
                      Invite to channel
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem
                  onClick={() => actions.toggleMute(channel)}
                  className="cursor-pointer"
                >
                  <BellSlash size={16} className="mr-2" />
                  {isMuted ? "Unmute channel" : "Mute channel"}
                </DropdownMenuItem>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="cursor-pointer">
                    <Bell size={16} className="mr-2" />
                    Notifications: {membership?.notify || "all"}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {["all", "mentions", "none"].map((level) => (
                      <DropdownMenuItem
                        key={level}
                        onClick={() => actions.notifyChange(channel, level)}
                        className={cn(
                          "cursor-pointer capitalize",
                          (membership?.notify || "all") === level &&
                            "font-bold",
                        )}
                      >
                        {level}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
                {canManage && (
                  <DropdownMenuItem
                    onClick={() => actions.toggleArchive(channel)}
                    className="cursor-pointer"
                  >
                    <Archive size={16} className="mr-2" />
                    {channel.is_archived
                      ? "Unarchive channel"
                      : "Archive channel"}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => actions.leave(channel)}
                  className="cursor-pointer"
                >
                  <SignOut size={16} className="mr-2" />
                  Leave channel
                </DropdownMenuItem>
                {canDelete && (
                  <DropdownMenuItem
                    onClick={() => actions.remove(channel)}
                    className="text-destructive cursor-pointer"
                  >
                    <Trash size={16} className="mr-2" />
                    Delete channel
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-56">
        <ContextMenuItem onSelect={() => actions.toggleMute(channel)}>
          {isMuted ? "Unmute Channel" : "Mute Channel"}
        </ContextMenuItem>
        <ContextMenuSub>
          <ContextMenuSubTrigger>
            Notifications: {membership?.notify || "all"}
          </ContextMenuSubTrigger>
          <ContextMenuSubContent>
            {["all", "mentions", "none"].map((level) => (
              <ContextMenuItem
                key={level}
                onSelect={() => actions.notifyChange(channel, level)}
                className={cn(
                  "capitalize",
                  (membership?.notify || "all") === level && "font-bold",
                )}
              >
                {level}
              </ContextMenuItem>
            ))}
          </ContextMenuSubContent>
        </ContextMenuSub>
        <ContextMenuSeparator />
        <ContextMenuItem onSelect={handleCopyLink}>
          Copy Link to Channel
        </ContextMenuItem>
        {canManage && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem onSelect={() => actions.edit(channel)}>
              Channel Details…
            </ContextMenuItem>
            <ContextMenuItem onSelect={() => actions.invite(channel)}>
              Invite People…
            </ContextMenuItem>
            <ContextMenuItem onSelect={() => actions.toggleArchive(channel)}>
              {channel.is_archived ? "Unarchive Channel" : "Archive Channel"}
            </ContextMenuItem>
          </>
        )}
        <ContextMenuSeparator />
        <ContextMenuItem
          onSelect={() => actions.leave(channel)}
          className="text-destructive focus:text-destructive"
        >
          Leave Channel…
        </ContextMenuItem>
        {canDelete && (
          <ContextMenuItem
            onSelect={() => actions.remove(channel)}
            className="text-destructive focus:text-destructive"
          >
            Delete Channel…
          </ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
});
